import { useCallback, useState } from "react";

/**
 * Tracks the user's hand-picked "important" molecules by BSA-DB ID.
 * Independent of the current page, so a selection made on one page survives
 * paging to another — it's a standing list the user curates. It does NOT
 * survive a molecule dropping out of the active filters, though: callers
 * should prune it (via `pruneToVisible`) against the freshly filtered
 * result whenever the filter criteria change, so a molecule that no longer
 * matches can't stay selected forever.
 */
export const useSelectedMolecules = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((existing) => existing !== id)
        : [...prev, id],
    );
  };

  const clearSelection = () => setSelectedIds([]);

  /** Drops any selected id not present in `visibleIds`. No-op if nothing changes. */
  const pruneToVisible = useCallback((visibleIds: Set<string>) => {
    setSelectedIds((prev) => {
      const next = prev.filter((id) => visibleIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  return { selectedIds, toggleSelection, clearSelection, pruneToVisible };
};
